import type { MoveBpaTarget } from "../../../../api/pipelineMoveBpa.service.js";
import { parseCsvRecords, type CsvRecord } from "../BatchUpdates/parseCsvRecords.js";

export const REQUIRED_COLUMNS = [
  "firm_ref",
  "publication_id",
  "source_practice_area_ids",
  "target_practice_area_id",
  "target_practice_area_name",
  "target_table_id",
  "target_table_name",
] as const;

export const OPTIONAL_COLUMNS = ["firm_name", "expected_items", "note"] as const;

export type MoveBpaCsvRow = {
  line: number;
  firmRef: string;
  firmName: string | null;
  publicationId: number;
  sourcePracticeAreaIds: number[];
  target: MoveBpaTarget;
  expectedItems: number | null;
  note: string | null;
};

export type InvalidMoveBpaRow = { line: number; reason: string };

export type ParsedMoveBpaCsv = { valid: MoveBpaCsvRow[]; invalid: InvalidMoveBpaRow[] };

type Field = (name: string) => string;

type Classified = { row: MoveBpaCsvRow } | { reason: string };

const DIGITS_PATTERN = /^\d+$/;

function isInteger(value: string): boolean {
  return DIGITS_PATTERN.test(value) && Number.isSafeInteger(Number(value));
}

function parseInteger(field: Field, column: string): number | string {
  const value = field(column);
  return isInteger(value) ? Number(value) : `${column} must be an integer`;
}

function parseIdList(field: Field, column: string): number[] | string {
  const parts = field(column)
    .replace(/;$/, "")
    .split(";")
    .map(part => part.trim());
  if (!parts.every(isInteger)) return `${column} must be a ;-separated list of integers`;
  return [...new Set(parts.map(Number))];
}

function parseOptionalInteger(field: Field, column: string): number | null | string {
  return field(column) === "" ? null : parseInteger(field, column);
}

function optionalText(field: Field, column: string): string | null {
  const value = field(column);
  return value === "" ? null : value;
}

function classify(field: Field, line: number): Classified {
  const firmRef = field("firm_ref");
  if (!DIGITS_PATTERN.test(firmRef)) return { reason: "firm_ref must be an integer" };
  const publicationId = parseInteger(field, "publication_id");
  if (typeof publicationId === "string") return { reason: publicationId };
  const sourceIds = parseIdList(field, "source_practice_area_ids");
  if (typeof sourceIds === "string") return { reason: sourceIds };
  const practiceAreaId = parseInteger(field, "target_practice_area_id");
  if (typeof practiceAreaId === "string") return { reason: practiceAreaId };
  if (!sourceIds.includes(practiceAreaId)) {
    return { reason: "source_practice_area_ids must contain target_practice_area_id" };
  }
  const practiceAreaName = field("target_practice_area_name");
  if (practiceAreaName === "") return { reason: "target_practice_area_name must not be empty" };
  const tableId = parseInteger(field, "target_table_id");
  if (typeof tableId === "string") return { reason: tableId };
  const tableName = field("target_table_name");
  if (tableName === "") return { reason: "target_table_name must not be empty" };
  const expectedItems = parseOptionalInteger(field, "expected_items");
  if (typeof expectedItems === "string") return { reason: expectedItems };

  return {
    row: {
      line,
      firmRef,
      firmName: optionalText(field, "firm_name"),
      publicationId,
      sourcePracticeAreaIds: sourceIds,
      target: { practiceAreaId, practiceAreaName, tableId, tableName },
      expectedItems,
      note: optionalText(field, "note"),
    },
  };
}

function conflictReason(row: MoveBpaCsvRow, accepted: MoveBpaCsvRow[]): string | null {
  for (const other of accepted) {
    if (other.firmRef !== row.firmRef || other.publicationId !== row.publicationId) continue;
    if (other.target.practiceAreaId === row.target.practiceAreaId) {
      return `duplicate of line ${other.line} (same firm_ref, publication_id and target_practice_area_id)`;
    }
    if (row.sourcePracticeAreaIds.some(id => other.sourcePracticeAreaIds.includes(id))) {
      return `source_practice_area_ids overlap line ${other.line}, which has a different target_practice_area_id`;
    }
  }
  return null;
}

function classifyRecords(records: CsvRecord[]): ParsedMoveBpaCsv {
  const valid: MoveBpaCsvRow[] = [];
  const invalid: InvalidMoveBpaRow[] = [];

  for (const { line, raw, parseError } of records) {
    if (parseError) {
      invalid.push({ line, reason: parseError });
      continue;
    }
    const field: Field = name => (raw[name] ?? "").trim();
    const classified = classify(field, line);
    if ("reason" in classified) {
      invalid.push({ line, reason: classified.reason });
      continue;
    }
    const conflict = conflictReason(classified.row, valid);
    if (conflict) {
      invalid.push({ line, reason: conflict });
      continue;
    }
    valid.push(classified.row);
  }

  return { valid, invalid };
}

export async function parseMoveBpaCsv(input: File | string): Promise<ParsedMoveBpaCsv> {
  return classifyRecords(await parseCsvRecords(input, REQUIRED_COLUMNS));
}
